import type {
  DeployContext,
  MockTokenAddresses,
  UniswapDeployment,
} from "@gotts.ai/devenv";
import type { Address, PublicClient, TestClient, WalletClient } from "viem";

/** Context for scenario execution. */
export interface ScenarioContext {
  publicClient: PublicClient;
  walletClient: WalletClient;
  testClient: TestClient;
  deployment: UniswapDeployment;
  onProgress?: (message: string) => void;
}

/** Convert ScenarioContext to a DeployContext for devenv seed functions. */
function toDeployContext(ctx: ScenarioContext): DeployContext {
  const deployer = "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266" as Address;
  return {
    publicClient: ctx.publicClient,
    walletClient: ctx.walletClient,
    deployer,
    request: async (args: { method: string; params: unknown[] }) =>
      ctx.testClient.request(args as never),
  };
}

/** Extract MockTokenAddresses from a UniswapDeployment. */
function toTokenAddresses(deployment: UniswapDeployment): MockTokenAddresses {
  return (deployment.tokens ?? {}) as unknown as MockTokenAddresses;
}

/** Scenario definition. */
export interface Scenario {
  id: string;
  name: string;
  description: string;
  category: "trading" | "liquidity" | "stress" | "agent";
  estimatedTime: string;
  execute: (ctx: ScenarioContext) => Promise<void>;
}

/** Built-in scenarios. */
export const SCENARIOS: Scenario[] = [
  {
    id: "basic-swap",
    name: "Basic Swap",
    description:
      "Execute a simple ETH → USDC swap through V3, demonstrating the swap flow and price impact.",
    category: "trading",
    estimatedTime: "~2s",
    execute: async (ctx) => {
      ctx.onProgress?.("Executing ETH → USDC swap via V3 Router...");
      // Use devenv seed swap helpers when available
      const { seedSwaps } = await import("../seed/index.js");
      await seedSwaps(
        toDeployContext(ctx),
        ctx.deployment,
        toTokenAddresses(ctx.deployment),
      );
      ctx.onProgress?.("Swap complete");
    },
  },
  {
    id: "add-liquidity",
    name: "Add Liquidity",
    description:
      "Provide concentrated liquidity to a V3 WETH/USDC pool across a tight tick range.",
    category: "liquidity",
    estimatedTime: "~3s",
    execute: async (ctx) => {
      ctx.onProgress?.("Adding liquidity to WETH/USDC V3 pool...");
      const { seedLiquidity } = await import("../seed/index.js");
      await seedLiquidity(
        toDeployContext(ctx),
        ctx.deployment,
        toTokenAddresses(ctx.deployment),
      );
      ctx.onProgress?.("Liquidity added");
    },
  },
  {
    id: "multi-hop-swap",
    name: "Multi-Hop Swap",
    description:
      "Execute a multi-hop swap: DAI → USDC → WETH through two V3 pools, showing path routing.",
    category: "trading",
    estimatedTime: "~3s",
    execute: async (ctx) => {
      ctx.onProgress?.("Executing multi-hop DAI → USDC → WETH...");
      // Placeholder — will be implemented with Universal Router encoding
      await new Promise((r) => setTimeout(r, 1000));
      ctx.onProgress?.("Multi-hop swap complete");
    },
  },
  {
    id: "stress-test",
    name: "Stress Test",
    description:
      "Execute 100 rapid swaps to stress test the Tevm instance and verify block production.",
    category: "stress",
    estimatedTime: "~10s",
    execute: async (ctx) => {
      ctx.onProgress?.("Running 100 rapid swaps...");
      for (let i = 0; i < 100; i++) {
        await ctx.testClient.mine({ blocks: 1 });
        if (i % 10 === 0) {
          ctx.onProgress?.(`Mined ${String(i + 1)}/100 blocks...`);
        }
      }
      ctx.onProgress?.("Stress test complete");
    },
  },
];

/** Get a scenario by ID. */
export function getScenario(id: string): Scenario | undefined {
  return SCENARIOS.find((s) => s.id === id);
}
